import { useState } from "react";
import { Check, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useLook } from "@/context/LookContext";
import { api, errMsg } from "@/lib/api";
import { deriveCustom, PRESETS, themeOf } from "@/lib/themes";
import ThemeEditor from "@/components/look/ThemeEditor";

const SECTIONS = ["home", "money", "kids", "calendar", "connect", "media", "family"];
const LIMIT = "You can keep up to 5 custom themes. Delete one to add another.";

export function ThemeSwatch({ t }) {
  const rgb = (v) => `rgb(${v})`;
  return (
    <div className="overflow-hidden rounded-lg border" style={{ background: rgb(t.bg), borderColor: rgb(t.line) }}>
      <div className="flex gap-2 p-3">
        <div className="w-8 space-y-1.5 rounded-md p-1.5" style={{ background: rgb(t.bg), boxShadow: `inset -1px 0 0 ${rgb(t.border)}` }}>
          {SECTIONS.slice(0, 4).map((s) => <div key={s} className="h-1.5 rounded-full" style={{ background: `rgb(${t.glows[s]})` }} />)}
        </div>
        <div className="flex-1 rounded-md border p-2.5" style={{ background: rgb(t.surface), borderColor: rgb(t.border) }}>
          <div className="text-[11px] font-bold" style={{ color: rgb(t.text) }}>Aa Hello</div>
          <div className="mt-0.5 text-[9px]" style={{ color: rgb(t.muted) }}>Bills due this week</div>
          <div className="mt-2 inline-block rounded px-1.5 py-0.5 text-[8px] font-bold" style={{ background: rgb(t.accent), color: rgb(t.accentInk) }}>Button</div>
        </div>
      </div>
      <div className="flex gap-1 px-3 pb-2.5">
        {SECTIONS.map((s) => <span key={s} className="h-2 w-2 rounded-full" style={{ background: `rgb(${t.glows[s]})` }} />)}
      </div>
    </div>
  );
}

function ThemeCard({ t, active, onPick, testid, children }) {
  return (
    <div className="group relative rounded-xl border p-3 transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-0.5"
      style={{ borderColor: active ? "rgb(var(--q-accent))" : "rgb(var(--q-border))", background: "rgb(var(--q-surface))", boxShadow: active ? "0 0 0 1px rgb(var(--q-accent))" : "none" }}
      data-testid={testid} data-active={active ? "1" : "0"}>
      <button className="block w-full text-left" onClick={onPick} data-testid={`${testid}-pick`} aria-pressed={active}>
        <ThemeSwatch t={t} />
        <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <span className="font-display text-sm font-bold text-q-text" data-testid={`${testid}-label`}>{t.label}</span>
          {active && <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.14em] text-q-accent" data-testid={`${testid}-current`}><Check className="h-3 w-3" /> Current</span>}
        </div>
      </button>
      {children}
    </div>
  );
}

export default function ThemeTab() {
  const { look, update, replace } = useLook();
  const [editing, setEditing] = useState(null);
  const [limitMsg, setLimitMsg] = useState("");
  const customs = look.custom_themes || [];

  const makeOwn = () => {
    if (customs.length >= 5) { setLimitMsg(LIMIT); return; }
    setLimitMsg("");
    setEditing({ start: themeOf(look) });
  };
  const remove = async (ct) => {
    try {
      const { data } = await api.delete(`/me/custom-themes/${ct.id}`);
      replace(data);
      setLimitMsg("");
      toast.success(`Deleted "${ct.name}"`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-10" data-testid="theme-tab">
      <section>
        <h2 className="q-eyebrow mb-4">Ready-made themes</h2>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-6">
          {PRESETS.map((t) => (
            <ThemeCard key={t.id} t={t} active={look.theme === t.id} onPick={() => update({ theme: t.id })} testid={`theme-card-${t.id}`} />
          ))}
        </div>
      </section>
      <section>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="q-eyebrow">Your themes · {customs.length} of 5</h2>
          <button className="q-btn q-btn-primary" onClick={makeOwn} data-testid="theme-make-own-btn"><Plus className="h-4 w-4" /> Make my own</button>
        </div>
        {limitMsg && <p className="mb-4 rounded-lg border border-q-line bg-q-raised px-3.5 py-2.5 text-sm font-semibold text-q-text" data-testid="custom-theme-limit-msg">{limitMsg}</p>}
        {customs.length === 0 ? (
          <p className="rounded-xl border border-dashed border-q-line px-5 py-8 text-center text-sm text-q-muted" data-testid="custom-themes-empty">Pick your own background, card, text and accent colours.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-5">
            {customs.map((ct) => {
              const t = deriveCustom(ct);
              return (
                <ThemeCard key={ct.id} t={t} active={look.theme === `custom:${ct.id}`} onPick={() => update({ theme: `custom:${ct.id}` })} testid={`custom-theme-card-${ct.id}`}>
                  <div className="mt-2 flex gap-1">
                    <button className="q-btn q-btn-ghost px-2 py-1 text-xs" onClick={() => setEditing({ ct })} data-testid={`custom-theme-edit-btn-${ct.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</button>
                    <button className="q-btn q-btn-ghost px-2 py-1 text-xs hover:text-red-300" onClick={() => remove(ct)} data-testid={`custom-theme-delete-btn-${ct.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                  </div>
                </ThemeCard>
              );
            })}
          </div>
        )}
      </section>
      {editing && <ThemeEditor editing={editing} onClose={() => setEditing(null)} onSaved={(l) => { replace(l); setEditing(null); }} onLimit={setLimitMsg} />}
    </div>
  );
}
